import Microgame from '../engine/Microgame.js';

export default class PianoGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: 'Piano !', en: 'Piano!', es: '¡Piano!' },
            controls: { fr: 'Appuie sur la touche correspondante au bon moment', en: 'Tap the matching key at the right time', es: 'Toca la tecla correspondiente en el momento adecuado' },
            objective: { fr: 'Attrape la note quand elle passe dans la zone lumineuse', en: 'Catch the note when it passes through the glowing zone', es: 'Atrapa la nota cuando pase por la zona brillante' },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>`,
            actions: [
                { type: 'wait', duration: 900 },
                { type: 'tap', x: 50, y: 84, duration: 220 },
                { type: 'wait', duration: 720 },
            ],
            loopDuration: 2900
        };
    }
    constructor(engine) {
        super(engine);
        this.keys = [];
        this.targetKeyIndex = -1;
        this.hit = false;

        const speedMult = engine.speedMultiplier || 1.0;
        
        // Ensure duration is set appropriately
        this.duration = 4000; // Divided by GameEngine

        // Scaling such that speedMult 4 equals exactly 3.6 effective speed
        const adj = 1 + (speedMult - 1) * (2.6 / 3.0); 
        this.speed = 1.2 * adj;
        
        this._isAnimating = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "PLAY!";

        // Target Zone config
        this.zoneTopPercent = 74; // bottom 20% of an 80% key starting at 10%
        this.zoneBottomPercent = 90;

        // Create 5 keys
        const keyWidth = 18; // %
        const gap = 2; // %
        const startX = 1;

        for (let i = 0; i < 5; i++) {
            const key = document.createElement('div');
            key.style.position = 'absolute';
            key.style.width = `${keyWidth}%`;
            key.style.height = '80%';
            key.style.bottom = '10%';
            key.style.left = `${startX + i * (keyWidth + gap)}%`;
            key.style.background = '#222'; // Dark keys
            key.style.border = `2px solid #444`;
            key.style.borderRadius = '0 0 5px 5px';
            key.style.boxShadow = `0 0 5px #000`;
            this.container.appendChild(key);
            this.keys.push(key);
        }

        // Pick a target key
        this.targetKeyIndex = this.isPlayback ? 2 : Math.floor(Math.random() * 5);
        const targetKey = this.keys[this.targetKeyIndex];

        // Highlight target key
        targetKey.style.background = '#333'; // Slightly lighter
        targetKey.style.border = `2px solid ${this.secondaryColor}`;
        targetKey.style.boxShadow = `0 0 15px ${this.secondaryColor}`;

        // Target Zone Indicator (Visual cue at bottom of key)
        const zone = document.createElement('div');
        zone.style.position = 'absolute';
        zone.style.width = '100%';
        zone.style.height = '20%'; // Bottom 20%
        zone.style.bottom = '0';
        zone.style.background = this.secondaryColor;
        zone.style.opacity = '0.5';
        zone.style.borderTop = `2px solid ${this.secondaryColor}`;
        zone.style.pointerEvents = 'none';
        targetKey.appendChild(zone);

        // Note falling down to the key
        this.note = document.createElement('div');
        this.note.style.position = 'absolute';
        this.note.style.width = '30px';
        this.note.style.height = '30px';
        this.note.style.background = this.primaryColor;
        this.note.style.borderRadius = '50%';
        this.note.style.left = `${startX + this.targetKeyIndex * (keyWidth + gap) + keyWidth / 2}%`;
        this.note.style.top = '0%';
        this.note.style.transform = 'translateX(-50%)';
        this.note.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        this.note.style.zIndex = '10';
        this.container.appendChild(this.note);

        this.noteY = 0;
        this.lastTime = null;

        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);

        this.animate();
    }

    animate() {
        if (!this.active) return;
        
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
            requestAnimationFrame(() => this.animate());
            return;
        }

        const now = performance.now();
        if (!this.lastTime) this.lastTime = now;
        const dt = Math.min((now - this.lastTime) / 16.67, 3);
        this.lastTime = now;

        this.noteY += this.speed * dt;
        this.note.style.top = `${this.noteY}%`;

        // Target zone is roughly 80% to 100% of the key's height relative to container?
        // Key is bottom 10%, height 80%. Top of key is 10% from top. Bottom of key is 90% from top.
        // Note starts at 0%.
        // We want to hit when it's near the bottom of the key.
        // Let's say target is 80% - 90% of screen height.

        // Note falls completely off
        if (this.noteY > 105) {
            this.triggerEnd(false);
        }

        requestAnimationFrame(() => this.animate());
    }

    triggerEnd(winStatus) {
        if (this.won || this.lost || this._isAnimating) return;
        
        this._isAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        // Freeze for 100ms so user sees where it stopped
        setTimeout(() => {
            if (winStatus) this.win();
            else this.lose();
        }, 100);
    }

    onTap(e) {
        if (this.won || this.lost || this._isAnimating) return;

        // Visual mapping: Key is 10% to 90% vertically. Zone is 74% to 90%.
        const H = this.container.clientHeight || 700;
        const noteHeightPct = (30 / H) * 100;
        
        const noteTop = this.noteY;
        const noteBottom = this.noteY + noteHeightPct;

        // Check if note TOUCHES the target zone (easier than entirely inside)
        const isTouching = (noteBottom >= this.zoneTopPercent) && (noteTop <= this.zoneBottomPercent);

        if (isTouching) {
            // Check if tap is on the correct key column
            const rect = this.container.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width * 100;

            const keyWidth = 18;
            const gap = 2;
            const startX = 1;
            const targetLeft = startX + this.targetKeyIndex * (keyWidth + gap);
            const targetRight = targetLeft + keyWidth;

            if (x >= targetLeft && x <= targetRight) {
                this.hit = true;
                const pressedKey = this.keys[this.targetKeyIndex];
                pressedKey.style.transform = 'translateY(15px)';
                pressedKey.style.boxShadow = `inset 0 20px 30px rgba(0,0,0,0.9), 0 0 5px ${this.secondaryColor}`;
                pressedKey.style.background = '#111'; // Darker background to simulate depth
                
                // Move the note alongside the key to avoid disjoint visuals
                if (this.note) {
                    this.note.style.transform = 'translateX(-50%) translateY(15px)';
                }
                
                this.createEffect(e.clientX, e.clientY);
                this.triggerEnd(true);
            } else {
                // Wrong key column
                this.triggerEnd(false);
            }
        } else {
            // Mistimed
            this.triggerEnd(false);
        }
    }

    createEffect(x, y) {
        const burst = document.createElement('div');
        burst.style.position = 'absolute';
        burst.style.left = `${x}px`;
        burst.style.top = `${y}px`;
        burst.style.width = '100px';
        burst.style.height = '100px';
        burst.style.background = this.primaryColor;
        burst.style.borderRadius = '50%';
        burst.style.transform = 'translate(-50%, -50%) scale(0)';
        burst.style.opacity = '0.8';
        burst.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        document.body.appendChild(burst);

        burst.animate([
            { transform: 'translate(-50%, -50%) scale(0)', opacity: 0.8 },
            { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 }
        ], { duration: 300 }).onfinish = () => burst.remove();
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
